import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import PerformanceTimelineChart from "./PerformanceTimelineChart";
import getApiUrl from '../utils/getApiUrl';
import {
  TrendingUp, TrendingDown, RefreshCw, AlertTriangle, Target, Zap,
  ArrowUpRight, ArrowDownRight, Check, ChevronDown, ChevronUp,
  DollarSign, Search, Play, CheckCircle, XCircle, History,
  Ban, Plus, Crown, Eye, EyeOff, Crosshair, BarChart3,
  ArrowUp, ShieldAlert, Gauge, Wallet
} from "lucide-react";

const API = getApiUrl() + "/api";
const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, { ...opts, credentials: "include", headers: { ...(opts.headers || {}), "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

export default function BidOptimizerPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState({});
  const [applyingAll, setApplyingAll] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("conversions");
  const [showHistory, setShowHistory] = useState(false);
  const [appliedActions, setAppliedActions] = useState(new Set());
  const [kwFilter, setKwFilter] = useState("enabled");
  const [showAllKw, setShowAllKw] = useState(false);
  const [activeTab, setActiveTab] = useState("position");
  const [bidHistory, setBidHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showKwHistory, setShowKwHistory] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await authFetch(API + "/google-ads/keywords-performance");
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadBidHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await authFetch(API + "/google-ads/keyword-bid-history");
      if (res.ok) setBidHistory(await res.json());
    } catch (e) { console.error(e); }
    setLoadingHistory(false);
  };

  useEffect(() => {
    if (showKwHistory && !bidHistory) loadBidHistory();
  }, [showKwHistory]);

  const applyBidIncrease = async (kw) => {
    const key = `bid-${kw.criterion_id}`;
    if (appliedActions.has(key)) return;
    const estCpc = kw.estimated_cpc_for_top || kw.first_position_cpc || 0;
    if (!estCpc) return toast.error("Pas d'estimation CPC disponible pour ce mot-cle");
    setApplying(p => ({ ...p, [key]: true }));
    toast.info(`Enchere "${kw.text}" : ${kw.avg_cpc}€ → ${estCpc}€...`);
    try {
      const res = await authFetch(API + "/google-ads/apply-bid-change", {
        method: "POST",
        body: JSON.stringify({
          action: "increase_keyword_bid",
          criterion_id: kw.criterion_id,
          ad_group_id: kw.ad_group_id,
          campaign_id: kw.campaign_id,
          term: kw.text,
          old_cpc: kw.avg_cpc,
          new_cpc: estCpc,
          metrics_snapshot: {
            abs_top_pct: kw.abs_top_pct, top_pct: kw.top_pct,
            avg_cpc: kw.avg_cpc, conversions: kw.conversions,
            cost: kw.cost, clicks: kw.clicks, cpa: kw.cpa,
            impressions: kw.impressions,
          }
        })
      });
      const result = await res.json();
      if (result.applied) {
        setAppliedActions(p => new Set([...p, key]));
        setBidHistory(null);
        toast.success(`${kw.text}: ${result.message}`);
      } else {
        toast.error(result.message || "Erreur lors de l'application");
      }
    } catch (e) { toast.error("Erreur: " + e.message); }
    setApplying(p => ({ ...p, [key]: false }));
  };

  const applyAction = async (rec, idx) => {
    setApplying(p => ({ ...p, [idx]: true }));
    try {
      const res = await authFetch(API + "/google-ads/apply-bid-change", { method: "POST", body: JSON.stringify(rec) });
      const result = await res.json();
      if (result.applied) {
        setAppliedActions(p => new Set([...p, idx]));
        toast.success(result.message);
      } else {
        toast.error(result.message || "Erreur");
      }
    } catch (e) { toast.error("Erreur: " + e.message); }
    setApplying(p => ({ ...p, [idx]: false }));
  };

  const applyAllOfType = async (type) => {
    if (!data?.recommendations) return;
    const recs = data.recommendations.filter(r => r.type === type && !appliedActions.has(`${type}-${r.term || r.campaign_name}`));
    if (!recs.length) return;
    setApplyingAll(true);
    toast.info(`Application de ${recs.length} recommandations...`);
    try {
      const res = await authFetch(API + "/google-ads/apply-all-recommendations", { method: "POST", body: JSON.stringify({ type, recommendations: recs }) });
      const result = await res.json();
      toast.success(`${result.applied}/${result.total} appliquees`);
      const newSet = new Set(appliedActions);
      recs.forEach(r => newSet.add(`${type}-${r.term || r.campaign_name}`));
      setAppliedActions(newSet);
    } catch (e) { toast.error("Erreur: " + e.message); }
    setApplyingAll(false);
  };

  const filteredTerms = useMemo(() => {
    if (!data?.search_terms) return [];
    let terms = [...data.search_terms];
    if (filter === "converting") terms = terms.filter(t => t.conversions > 0);
    else if (filter === "wasted") terms = terms.filter(t => t.conversions === 0 && t.clicks > 0);
    else if (filter === "top") terms = terms.filter(t => t.score >= 7);
    else if (filter === "not_first") terms = terms.filter(t => t.abs_top_pct < 50 && t.conversions > 0);
    if (sortBy === "conversions") terms.sort((a, b) => b.conversions - a.conversions || b.clicks - a.clicks);
    else if (sortBy === "cost") terms.sort((a, b) => b.cost - a.cost);
    else if (sortBy === "cpa") terms.sort((a, b) => (a.cpa || 999) - (b.cpa || 999));
    else if (sortBy === "score") terms.sort((a, b) => b.score - a.score);
    else if (sortBy === "position") terms.sort((a, b) => b.abs_top_pct - a.abs_top_pct);
    return terms;
  }, [data, filter, sortBy]);

  const filteredKw = useMemo(() => {
    if (!data?.keywords) return [];
    let kws = [...data.keywords];
    if (kwFilter === "enabled") kws = kws.filter(k => k.status === "ENABLED");
    else if (kwFilter === "paused") kws = kws.filter(k => k.status === "PAUSED");
    else if (kwFilter === "action") kws = kws.filter(k => k.recommendation && k.recommendation.action !== "keep");
    return kws;
  }, [data, kwFilter]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
      <p className="text-sm text-slate-400">Analyse des positions en cours...</p>
    </div>
  );
  if (!data) return null;

  const s = data.summary || {};
  const pd = data.position_diagnostic || {};
  const budgetRecs = (data.recommendations || []).filter(r => r.type === "increase_budget");
  const cpaRecs = (data.recommendations || []).filter(r => r.type === "campaign_cpa");
  const boostRecs = (data.recommendations || []).filter(r => r.type === "boost_keyword");
  const excludeRecs = (data.recommendations || []).filter(r => r.type === "exclude_keyword");
  const displayedKw = showAllKw ? filteredKw : filteredKw.slice(0, 30);
  const ks = data.keywords_summary || {};
  const QS_COLORS = { 10: "text-green-400", 9: "text-green-400", 8: "text-green-300", 7: "text-lime-300", 6: "text-yellow-300", 5: "text-amber-300", 4: "text-orange-300", 3: "text-red-300", 2: "text-red-400", 1: "text-red-500" };
  const SCORE_COLORS = { 10: "bg-green-500", 9: "bg-green-500", 8: "bg-green-400", 7: "bg-lime-400", 6: "bg-yellow-400", 5: "bg-amber-400", 4: "bg-orange-400", 3: "bg-red-400", 2: "bg-red-500", 1: "bg-red-600" };

  return (
    <div className="space-y-6" data-testid="bid-optimizer-panel">
      {/* HERO DIAGNOSTIC */}
      <PositionDiagnostic pd={pd} s={s} onRefresh={loadData} loading={loading} />

      {/* TAB NAVIGATION */}
      <div className="flex gap-1 bg-slate-900/60 rounded-xl p-1 border border-slate-700/40">
        {[
          ["position", "Position & Visibilite", <Crown className="w-4 h-4" />],
          ["actions", "Plan d'action", <Zap className="w-4 h-4" />],
          ["timeline", "Evolution", <TrendingUp className="w-4 h-4" />],
          ["keywords", "Mots-cles", <Target className="w-4 h-4" />],
          ["search", "Search Terms", <Search className="w-4 h-4" />],
        ].map(([key, label, icon]) => (
          <button key={key} onClick={() => setActiveTab(key)} data-testid={`tab-${key}`}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === key
                ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                : "text-slate-400 hover:text-slate-300 hover:bg-slate-800/50"
            }`}>
            {icon} {label}
          </button>
        ))}
      </div>

      {/* TAB: POSITION & VISIBILITY */}
      {activeTab === "position" && (
        <div className="space-y-5">
          {/* Position KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <PositionKPI
              icon={<Crown className="w-5 h-5" />}
              label="Position #1"
              value={`${pd.avg_position_1_pct || 0}%`}
              sub="du temps en 1ere position"
              status={pd.avg_position_1_pct >= 50 ? "good" : pd.avg_position_1_pct >= 20 ? "warn" : "bad"}
            />
            <PositionKPI
              icon={<Eye className="w-5 h-5" />}
              label="Part d'impressions"
              value={`${pd.avg_impression_share || 0}%`}
              sub="des recherches affichees"
              status={pd.avg_impression_share >= 70 ? "good" : pd.avg_impression_share >= 40 ? "warn" : "bad"}
            />
            <PositionKPI
              icon={<ShieldAlert className="w-5 h-5" />}
              label="Perdu (budget)"
              value={`${pd.max_budget_lost_pct || 0}%`}
              sub="impressions manquees"
              status={pd.max_budget_lost_pct <= 10 ? "good" : pd.max_budget_lost_pct <= 30 ? "warn" : "bad"}
              invert
            />
            <PositionKPI
              icon={<Gauge className="w-5 h-5" />}
              label="Perdu (rang)"
              value={`${pd.max_rank_lost_pct || 0}%`}
              sub="qualite/enchere faible"
              status={pd.max_rank_lost_pct <= 10 ? "good" : pd.max_rank_lost_pct <= 30 ? "warn" : "bad"}
              invert
            />
          </div>

          {/* Campaign Position Cards */}
          <div className="space-y-4">
            {(data.campaigns || []).filter(c => c.channel_type === "SEARCH" || c.impression_share > 0).map(c => {
              const budgetRec = budgetRecs.find(r => r.campaign_id === c.id);
              return (
                <CampaignPositionCard key={c.id} campaign={c}
                  budgetRec={budgetRec}
                  onApplyBudget={budgetRec ? () => {
                    const key = `increase_budget-${c.name}`;
                    applyAction({
                      action: "increase_daily_budget",
                      campaign_id: c.id,
                      campaign_name: c.name,
                      budget_resource_name: budgetRec.budget_resource_name,
                      current_value: budgetRec.current_value,
                      recommended_value: budgetRec.recommended_value,
                      metrics_snapshot: budgetRec.metrics_snapshot || {},
                    }, key);
                  } : null}
                  budgetApplied={appliedActions.has(`increase_budget-${c.name}`)}
                  budgetApplying={applying[`increase_budget-${c.name}`]}
                />
              );
            })}
            {(data.campaigns || []).filter(c => c.channel_type !== "SEARCH" && !c.impression_share).length > 0 && (
              <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
                <p className="text-xs text-slate-500 flex items-center gap-2">
                  <EyeOff className="w-4 h-4" />
                  {(data.campaigns || []).filter(c => c.channel_type !== "SEARCH" && !c.impression_share).length} campagne(s) non-Search masquee(s) (Display, Video, etc.)
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB: ACTION PLAN */}
      {activeTab === "actions" && (
        <div className="space-y-5">
          {/* Priority Action Summary */}
          <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-xl p-5">
            <h3 className="font-semibold text-base flex items-center gap-2 mb-4 text-slate-200">
              <Crosshair className="w-5 h-5 text-amber-400" />
              Plan d'action pour etre #1
            </h3>
            <div className="space-y-3">
              <ActionPriority
                num={1}
                title="Augmenter le budget"
                desc={budgetRecs.length > 0 ? `${budgetRecs.length} campagne(s) limitee(s) par le budget` : "Aucune limitation de budget detectee"}
                active={budgetRecs.length > 0}
                critical={budgetRecs.length > 0}
              />
              <ActionPriority
                num={2}
                title="Ajuster les Target CPA"
                desc={cpaRecs.length > 0 ? `${cpaRecs.length} campagne(s) avec CPA surdimensionne` : "Target CPA alignes"}
                active={cpaRecs.length > 0}
              />
              <ActionPriority
                num={3}
                title="Booster les keywords performants"
                desc={boostRecs.length > 0 ? `${boostRecs.length} terme(s) a ajouter en exact match` : "Deja optimise"}
                active={boostRecs.length > 0}
              />
              <ActionPriority
                num={4}
                title="Exclure les termes non-rentables"
                desc={excludeRecs.length > 0 ? `${excludeRecs.length} terme(s), economie ~${excludeRecs.reduce((s, r) => s + (r.wasted_cost || 0), 0).toFixed(0)}€/mois` : "Aucun terme a exclure"}
                active={excludeRecs.length > 0}
              />
            </div>
          </div>

          {/* Budget Increase Recommendations - CRITICAL */}
          {budgetRecs.length > 0 && (
            <div className="bg-red-950/30 border-2 border-red-500/30 rounded-xl overflow-hidden" data-testid="budget-recs-section">
              <div className="px-6 py-4 border-b border-red-500/20 flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2 text-base text-red-300">
                  <Wallet className="w-5 h-5" />
                  Augmentation Budget
                  <span className="text-xs px-2 py-0.5 rounded bg-red-500/30 text-red-200 ml-1 uppercase tracking-wider font-bold">Critique</span>
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {budgetRecs.map((rec, i) => {
                  const key = `increase_budget-${rec.campaign_name}`;
                  return (
                    <div key={i} className={`p-4 rounded-xl border ${appliedActions.has(key) ? "bg-green-500/10 border-green-500/25" : "bg-slate-900/50 border-red-500/15"}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-200 mb-2">{rec.campaign_name}</p>
                          <div className="flex items-center gap-4 mb-3">
                            <div className="text-center">
                              <p className="text-lg font-bold text-red-400">{rec.current_value}€</p>
                              <p className="text-[10px] text-slate-500">Budget actuel/jour</p>
                            </div>
                            <ArrowUpRight className="w-5 h-5 text-amber-400 shrink-0" />
                            <div className="text-center">
                              <p className="text-lg font-bold text-green-400">{rec.recommended_value}€</p>
                              <p className="text-[10px] text-slate-500">Budget recommande/jour</p>
                            </div>
                            <div className="h-8 w-px bg-slate-700 mx-1" />
                            <div className="text-center">
                              <p className="text-lg font-bold text-amber-300">{rec.budget_lost_pct}%</p>
                              <p className="text-[10px] text-slate-500">Impressions perdues</p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400">{rec.reason}</p>
                          <p className="text-xs text-green-400/80 mt-1">{rec.estimated_impact}</p>
                        </div>
                        <button onClick={() => applyAction({
                            action: "increase_daily_budget",
                            campaign_id: rec.campaign_id,
                            campaign_name: rec.campaign_name,
                            budget_resource_name: rec.budget_resource_name,
                            current_value: rec.current_value,
                            recommended_value: rec.recommended_value,
                            metrics_snapshot: rec.metrics_snapshot || {},
                          }, key)} disabled={appliedActions.has(key) || applying[key]}
                          className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium ${
                            appliedActions.has(key) ? "bg-green-500/20 text-green-300" : "bg-red-500/20 hover:bg-red-500/30 text-red-200"
                          } disabled:opacity-60`}
                          data-testid={`apply-budget-${rec.campaign_id}`}>
                          {applying[key] ? <RefreshCw className="w-4 h-4 animate-spin" /> :
                            appliedActions.has(key) ? <><Check className="w-4 h-4" /> Applique</> :
                            <><ArrowUp className="w-4 h-4" /> Augmenter</>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CPA Recommendations */}
          {cpaRecs.length > 0 && (
            <RecoSection title="Optimisation Target CPA" icon={<Target className="w-5 h-5 text-orange-400" />} color="orange" count={cpaRecs.length}
              onApplyAll={() => applyAllOfType("campaign_cpa")} applyingAll={applyingAll}>
              {cpaRecs.map((rec, i) => {
                const key = `campaign_cpa-${rec.campaign_name}`;
                return (
                  <RecoRow key={i} rec={rec} applied={appliedActions.has(key)} applying={applying[key]} onApply={() => applyAction(rec, key)}>
                    <div className="flex items-center gap-4">
                      <span className="text-red-400 line-through text-lg font-bold">{rec.current_value}€</span>
                      <ArrowDownRight className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 text-lg font-bold">{rec.recommended_value}€</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{rec.reason}</p>
                    <p className="text-xs text-green-400/80 mt-1">{rec.estimated_impact}</p>
                  </RecoRow>
                );
              })}
            </RecoSection>
          )}

          {/* Boost Recommendations */}
          {boostRecs.length > 0 && (
            <RecoSection title="Keywords a booster (exact match)" icon={<Plus className="w-5 h-5 text-green-400" />} color="green" count={boostRecs.length}
              onApplyAll={() => applyAllOfType("boost_keyword")} applyingAll={applyingAll}>
              {boostRecs.map((rec, i) => {
                const key = `boost_keyword-${rec.term}`;
                return (
                  <RecoRow key={i} rec={rec} applied={appliedActions.has(key)} applying={applying[key]} onApply={() => applyAction(rec, key)}>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-slate-200">"{rec.term}"</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300">CPA: {rec.current_cpa}€</span>
                      <span className="text-xs text-slate-400">{rec.conversions} conv.</span>
                    </div>
                  </RecoRow>
                );
              })}
            </RecoSection>
          )}

          {/* Exclude Recommendations */}
          {excludeRecs.length > 0 && (
            <RecoSection title="Termes a exclure (0 conversion)" icon={<Ban className="w-5 h-5 text-red-400" />} color="red" count={excludeRecs.length}
              totalSavings={excludeRecs.reduce((s, r) => s + (r.wasted_cost || 0), 0)}
              onApplyAll={() => applyAllOfType("exclude_keyword")} applyingAll={applyingAll}>
              {excludeRecs.map((rec, i) => {
                const key = `exclude_keyword-${rec.term}`;
                return (
                  <RecoRow key={i} rec={rec} applied={appliedActions.has(key)} applying={applying[key]} onApply={() => applyAction(rec, key)}>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-slate-200">"{rec.term}"</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300">{rec.wasted_cost}€ gaspilles</span>
                      <span className="text-xs text-slate-400">{rec.clicks} clics</span>
                    </div>
                  </RecoRow>
                );
              })}
            </RecoSection>
          )}

          {/* History */}
          {data.bid_history && data.bid_history.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
              <button onClick={() => setShowHistory(!showHistory)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/20"
                data-testid="toggle-history">
                <h3 className="font-semibold flex items-center gap-2 text-base"><History className="w-5 h-5 text-slate-400" /> Historique ({data.bid_history.length})</h3>
                {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showHistory && (
                <div className="px-6 pb-4 space-y-2">
                  {data.bid_history.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm py-2 border-b border-slate-700/20">
                      {h.result?.applied ? <CheckCircle className="w-4 h-4 text-green-400 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                      <span className="text-slate-400 text-xs w-32 shrink-0">{h.applied_at?.slice(0, 16)}</span>
                      <span className="text-slate-300 truncate">{h.result?.message || h.action}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {/* TAB: EVOLUTION / TIMELINE */}
      {activeTab === "timeline" && (
        <PerformanceTimelineChart />
      )}


      {/* TAB: KEYWORDS */}
      {activeTab === "keywords" && (
        <div className="space-y-5">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="keywords-table">
          <div className="px-6 py-4 border-b border-slate-700/30 flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-semibold flex items-center gap-2 text-base">
              <Target className="w-5 h-5 text-purple-400" />
              Mots-cles — Position & Encheres
              <span className="text-xs text-slate-400 font-normal ml-2">{ks.enabled || 0} actifs | {ks.paused || 0} en pause | {ks.with_quality_score || 0} avec QS</span>
            </h3>
            <div className="flex bg-slate-900/50 rounded-lg overflow-hidden text-xs">
              {[["enabled", `Actifs (${ks.enabled || 0})`], ["paused", `Pause (${ks.paused || 0})`], ["action", "Actions"], ["all", "Tous"]].map(([k, l]) => (
                <button key={k} onClick={() => setKwFilter(k)}
                  className={`px-3 py-1.5 ${kwFilter === k ? "bg-purple-500/20 text-purple-300" : "text-slate-400 hover:text-slate-300"}`}
                  data-testid={`kwfilter-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800 z-10">
                <tr className="text-left text-xs text-slate-500 border-b border-slate-700/30">
                  <th className="px-2 py-3">Statut</th>
                  <th className="px-2 py-3">Mot-cle</th>
                  <th className="px-2 py-3">Campagne</th>
                  <th className="px-2 py-3 text-center">QS</th>
                  <th className="px-2 py-3 text-center">Pos. #1</th>
                  <th className="px-2 py-3 text-center">Potentiel</th>
                  <th className="px-2 py-3 text-center">Haut page</th>
                  <th className="px-2 py-3 text-right">CPC actuel</th>
                  <th className="px-2 py-3 text-right">Enchère max</th>
                  <th className="px-2 py-3 text-right">CPC estime #1</th>
                  <th className="px-2 py-3 text-right">Ecart</th>
                  <th className="px-2 py-3 text-right">Conv.</th>
                  <th className="px-2 py-3 text-right">CPA</th>
                  <th className="px-2 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedKw.map((kw, i) => {
                  const key = `kw-${kw.criterion_id}`;
                  const isApplied = appliedActions.has(key);
                  const avgCpc = kw.avg_cpc || (kw.clicks > 0 ? +(kw.cost / kw.clicks).toFixed(2) : 0);
                  const cpcFor1 = kw.estimated_cpc_for_top || kw.first_position_cpc || 0;
                  const gap = avgCpc > 0 && cpcFor1 > 0 ? +(cpcFor1 - avgCpc).toFixed(2) : null;
                  const absPct = kw.abs_top_pct || 0;
                  const topPct = kw.top_pct || 0;
                  const potentialPct = kw.potential_abs_top_pct || absPct;
                  return (
                    <tr key={kw.criterion_id + "-" + i} className={`border-b border-slate-700/20 hover:bg-slate-700/20 ${
                      kw.recommendation?.action === "pause" ? "bg-red-500/5" :
                      gap !== null && gap > 0.5 ? "bg-amber-500/5" :
                      kw.conversions > 0 ? "bg-green-500/5" : ""
                    }`}>
                      <td className="px-2 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${kw.status === "ENABLED" ? "bg-green-500/20 text-green-300" : "bg-slate-600/30 text-slate-400"}`}>
                          {kw.status === "ENABLED" ? "ACTIF" : "PAUSE"}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <div className="text-slate-200 max-w-[180px] truncate font-medium text-xs">{kw.text}</div>
                        <div className="text-[10px] text-slate-500">{kw.match_type}</div>
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-400 max-w-[110px] truncate">{kw.campaign_name}</td>
                      <td className="px-2 py-2 text-center">
                        {kw.quality_score != null ? <span className={`font-bold ${QS_COLORS[kw.quality_score]}`}>{kw.quality_score}</span> : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <PositionBadge pct={absPct} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {potentialPct > absPct ? (
                          <span className="text-[11px] font-bold text-green-400">{potentialPct}%</span>
                        ) : absPct > 0 ? (
                          <span className="text-[11px] text-slate-500">=</span>
                        ) : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <PositionBadge pct={topPct} />
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-xs text-slate-300">{avgCpc > 0 ? `${avgCpc.toFixed(2)}€` : "-"}</td>
                      <td className="px-2 py-2 text-right font-mono text-xs">
                        {kw.bid > 0 ? (
                          <span className={`font-bold ${kw.bid > avgCpc ? "text-cyan-300" : "text-slate-400"}`}>{kw.bid.toFixed(2)}€</span>
                        ) : <span className="text-slate-600">auto</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-xs">
                        {cpcFor1 > 0 ? (
                          <span className="text-amber-300 font-bold">{cpcFor1.toFixed(2)}€</span>
                        ) : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-xs">
                        {gap !== null ? (
                          gap > 0 ? (
                            <span className="text-red-400">+{gap.toFixed(2)}€</span>
                          ) : (
                            <span className="text-green-400">{gap.toFixed(2)}€</span>
                          )
                        ) : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-bold" style={{ color: kw.conversions > 0 ? "#4ade80" : "#64748b" }}>
                        {kw.conversions > 0 ? (Number.isInteger(kw.conversions) ? kw.conversions : kw.conversions.toFixed(1)) : "-"}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-xs" style={{ color: kw.cpa ? (kw.cpa < 15 ? "#4ade80" : kw.cpa < 30 ? "#fbbf24" : "#ef4444") : "#64748b" }}>
                        {kw.cpa ? `${kw.cpa}€` : "-"}
                      </td>
                      <td className="px-2 py-2">
                        {(() => {
                          const bidKey = `bid-${kw.criterion_id}`;
                          const isBidApplied = appliedActions.has(bidKey);
                          const canApplyBid = (kw.estimated_cpc_for_top || kw.first_position_cpc) && kw.avg_cpc > 0 && gap !== null && gap > 0.3;
                          if (kw.recommendation && kw.recommendation.action !== "keep") {
                            const key = `kw-${kw.criterion_id}`;
                            const isApplied = appliedActions.has(key);
                            return (
                              <button onClick={() => applyAction({ action: kw.recommendation.action === "pause" ? "pause_keyword" : "enable_keyword", criterion_id: kw.criterion_id, ad_group_id: kw.ad_group_id, campaign_id: kw.campaign_id, term: kw.text }, key)}
                                disabled={isApplied || applying[key]} title={kw.recommendation.reason}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${isApplied ? "bg-green-500/20 text-green-300" : kw.recommendation.action === "pause" ? "bg-red-500/20 hover:bg-red-500/30 text-red-300" : "bg-green-500/20 hover:bg-green-500/30 text-green-300"} disabled:opacity-50`}
                                data-testid={`kw-action-${kw.criterion_id}`}>
                                {applying[key] ? <RefreshCw className="w-3 h-3 animate-spin" /> : isApplied ? <><Check className="w-3 h-3" /> OK</> : kw.recommendation.action === "pause" ? <><Ban className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Activer</>}
                              </button>
                            );
                          }
                          if (canApplyBid) {
                            return (
                              <button onClick={() => applyBidIncrease(kw)}
                                disabled={isBidApplied || applying[bidKey]}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${isBidApplied ? "bg-green-500/20 text-green-300" : "bg-amber-500/20 hover:bg-amber-500/30 text-amber-300"} disabled:opacity-50`}
                                data-testid={`bid-apply-${kw.criterion_id}`}>
                                {applying[bidKey] ? <RefreshCw className="w-3 h-3 animate-spin" /> :
                                  isBidApplied ? <><Check className="w-3 h-3" /> OK</> :
                                  <><ArrowUp className="w-3 h-3" /> {cpcFor1.toFixed(2)}€</>}
                              </button>
                            );
                          }
                          if (kw.conversions > 0 && (!gap || gap <= 0.3)) return <span className="text-[10px] text-green-400/60">OK</span>;
                          return null;
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Legend */}
          <div className="px-6 py-3 border-t border-slate-700/30 flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="font-semibold text-slate-400">CPC actuel</span> = CPC moyen paye (30j)
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="font-semibold text-cyan-400/70">Enchère max</span> = Enchere CPC max definie (reflete vos modifs)
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="font-semibold text-amber-400/70">CPC estime #1</span> = CPC necessaire pour ~90% en position #1
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="font-semibold text-green-400/70">Potentiel</span> = Position #1 estimee si budget suffisant
            </div>
          </div>
          {filteredKw.length > 30 && (
            <div className="px-6 py-3 border-t border-slate-700/30 text-center">
              <button onClick={() => setShowAllKw(!showAllKw)} className="text-xs text-slate-400 hover:text-slate-300 flex items-center gap-1 mx-auto"
                data-testid="show-all-kw">
                {showAllKw ? <><ChevronUp className="w-3 h-3" /> Voir moins</> : <><ChevronDown className="w-3 h-3" /> Voir les {filteredKw.length} mots-cles</>}
              </button>
            </div>
          )}
        </div>

        {/* Keyword Bid History */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="keyword-bid-history">
          <button onClick={() => { setShowKwHistory(!showKwHistory); if (!showKwHistory && !bidHistory) loadBidHistory(); }}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/20">
            <h3 className="font-semibold flex items-center gap-2 text-base">
              <History className="w-5 h-5 text-amber-400" />
              Historique des modifications
              {bidHistory?.summary?.total_tracked > 0 && (
                <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">{bidHistory.summary.total_tracked}</span>
              )}
            </h3>
            <div className="flex items-center gap-3">
              {bidHistory?.summary && (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-green-400">{bidHistory.summary.improved_position} pos. ameliorees</span>
                  <span className="text-blue-400">{bidHistory.summary.improved_conversions} conv. ameliorees</span>
                </div>
              )}
              {showKwHistory ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </button>
          {showKwHistory && (
            <div className="border-t border-slate-700/30">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8 gap-2">
                  <RefreshCw className="w-5 h-5 text-amber-400 animate-spin" />
                  <span className="text-sm text-slate-400">Chargement historique...</span>
                </div>
              ) : !bidHistory?.history?.length ? (
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-slate-400">Aucune modification enregistree.</p>
                  <p className="text-xs text-slate-500 mt-1">Cliquez sur les boutons d'enchere dans le tableau pour commencer le suivi.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800/80">
                      <tr className="text-left text-xs text-slate-500 border-b border-slate-700/30">
                        <th className="px-3 py-3">Date</th>
                        <th className="px-3 py-3">Mot-cle</th>
                        <th className="px-3 py-3 text-center">Enchere</th>
                        <th className="px-3 py-3 text-center">Position #1</th>
                        <th className="px-3 py-3 text-center">Conversions</th>
                        <th className="px-3 py-3 text-center">CPA</th>
                        <th className="px-3 py-3 text-center">CPC moyen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bidHistory.history.map((h, i) => {
                        const before = h.metrics_at_action || {};
                        const current = h.current_metrics || {};
                        const evo = h.evolution || {};
                        return (
                          <tr key={i} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                            <td className="px-3 py-3">
                              <div className="text-xs text-slate-300">{h.applied_at?.slice(0, 10)}</div>
                              <div className="text-[10px] text-slate-500">{h.applied_at?.slice(11, 16)}</div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-slate-200 font-medium text-xs max-w-[160px] truncate">{h.keyword_text}</div>
                              <div className="text-[10px] text-slate-500">{h.api_applied ? "API appliquee" : "Suivi"}</div>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xs text-slate-400">{h.old_cpc}€</span>
                                <ArrowUpRight className="w-3 h-3 text-amber-400" />
                                <span className="text-xs font-bold text-amber-300">{h.new_cpc}€</span>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <EvolutionCell before={before.abs_top_pct} current={current.abs_top_pct} delta={evo.abs_top_pct_delta} unit="%" />
                            </td>
                            <td className="px-3 py-3 text-center">
                              <EvolutionCell before={before.conversions} current={current.conversions} delta={evo.conversions_delta} />
                            </td>
                            <td className="px-3 py-3 text-center">
                              <EvolutionCell before={before.cpa} current={current.cpa} delta={evo.cpa_delta} unit="€" invert />
                            </td>
                            <td className="px-3 py-3 text-center">
                              <EvolutionCell before={before.avg_cpc} current={current.avg_cpc} delta={evo.avg_cpc_delta} unit="€" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {bidHistory?.history?.length > 0 && (
                <div className="px-6 py-3 border-t border-slate-700/30 flex items-center justify-between">
                  <p className="text-[10px] text-slate-500">Les metriques "actuelles" sont sur les 30 derniers jours. L'evolution sera visible apres quelques jours.</p>
                  <button onClick={loadBidHistory} disabled={loadingHistory}
                    className="text-xs text-slate-400 hover:text-slate-300 flex items-center gap-1">
                    <RefreshCw className={`w-3 h-3 ${loadingHistory ? "animate-spin" : ""}`} /> Rafraichir
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* TAB: SEARCH TERMS */}
      {activeTab === "search" && (
        <div className="space-y-5">
          {/* Performance KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <MiniKPI label="CPA Moyen" value={`${s.avg_cpa}€`} icon={<Target className="w-4 h-4" />} color="blue" />
            <MiniKPI label="CPC Moyen" value={`${s.avg_cpc}€`} icon={<DollarSign className="w-4 h-4" />} color="slate" />
            <MiniKPI label="Taux Conv." value={`${s.avg_conv_rate}%`} icon={<TrendingUp className="w-4 h-4" />} color="green" />
            <MiniKPI label="Gaspille" value={`${s.wasted_cost}€`} icon={<AlertTriangle className="w-4 h-4" />} color="red" />
            <MiniKPI label="Termes" value={`${s.converting_terms || 0}/${(data.search_terms || []).length}`} icon={<BarChart3 className="w-4 h-4" />} color="amber" />
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700/30 flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-semibold flex items-center gap-2 text-base">
                <Search className="w-5 h-5 text-blue-400" />
                Search Terms - Performance & Position
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex bg-slate-900/50 rounded-lg overflow-hidden text-xs">
                  {[["all", "Tous"], ["converting", "Convertissent"], ["not_first", "Pas #1"], ["wasted", "Gaspilles"], ["top", "Top Score"]].map(([k, l]) => (
                    <button key={k} onClick={() => setFilter(k)}
                      className={`px-3 py-1.5 ${filter === k ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-300"}`}
                      data-testid={`filter-${k}`}>{l}</button>
                  ))}
                </div>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="sort-select"
                  className="bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-300">
                  <option value="conversions">Tri: Conversions</option>
                  <option value="cost">Tri: Cout</option>
                  <option value="cpa">Tri: CPA</option>
                  <option value="score">Tri: Score</option>
                  <option value="position">Tri: Position #1</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="search-terms-table">
                <thead className="sticky top-0 bg-slate-800 z-10">
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-700/30">
                    <th className="px-3 py-3 w-8">Score</th>
                    <th className="px-3 py-3">Search Term</th>
                    <th className="px-3 py-3">Campagne</th>
                    <th className="px-3 py-3 text-right">CPC</th>
                    <th className="px-3 py-3 text-right">Cout</th>
                    <th className="px-3 py-3 text-right">Clics</th>
                    <th className="px-3 py-3 text-right">Conv.</th>
                    <th className="px-3 py-3 text-right">CPA</th>
                    <th className="px-3 py-3 text-center">Haut page</th>
                    <th className="px-3 py-3 text-center">Position #1</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTerms.slice(0, 100).map((t, i) => (
                    <tr key={i} className={`border-b border-slate-700/20 hover:bg-slate-700/20 ${t.conversions === 0 && t.cost > 5 ? "bg-red-500/5" : t.conversions > 0 && t.abs_top_pct < 30 ? "bg-amber-500/5" : t.score >= 7 ? "bg-green-500/5" : ""}`}>
                      <td className="px-3 py-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${SCORE_COLORS[t.score]}`}>{t.score}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-300 max-w-[220px] truncate">{t.term}</td>
                      <td className="px-3 py-2 text-xs text-slate-500 max-w-[120px] truncate">{t.campaign_name}</td>
                      <td className="px-3 py-2 text-right text-slate-300 font-mono text-xs">{t.cpc}€</td>
                      <td className="px-3 py-2 text-right text-slate-300 font-mono text-xs">{t.cost.toFixed(2)}€</td>
                      <td className="px-3 py-2 text-right text-slate-400">{t.clicks}</td>
                      <td className="px-3 py-2 text-right font-bold" style={{ color: t.conversions > 0 ? "#4ade80" : "#ef4444" }}>{t.conversions > 0 ? Math.round(t.conversions) : "0"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs" style={{ color: t.cpa ? (t.cpa < 15 ? "#4ade80" : t.cpa < 30 ? "#fbbf24" : "#ef4444") : "#64748b" }}>
                        {t.cpa ? `${t.cpa}€` : "-"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <PositionBadge pct={t.top_pct} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <PositionBadge pct={t.abs_top_pct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   SUB-COMPONENTS
   ═══════════════════════════════════════ */

function PositionDiagnostic({ pd, s, onRefresh, loading }) {
  const blocker = pd.primary_blocker;
  const severity = pd.max_budget_lost_pct > 40 ? "critical" : pd.max_budget_lost_pct > 20 ? "warning" : "ok";
  const posScore = pd.avg_position_1_pct || 0;

  return (
    <div className={`rounded-xl border overflow-hidden ${
      severity === "critical" ? "bg-gradient-to-r from-red-950/40 via-slate-900/80 to-slate-900/80 border-red-500/30" :
      severity === "warning" ? "bg-gradient-to-r from-amber-950/30 via-slate-900/80 to-slate-900/80 border-amber-500/30" :
      "bg-gradient-to-r from-green-950/30 via-slate-900/80 to-slate-900/80 border-green-500/30"
    }`} data-testid="position-diagnostic">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              severity === "critical" ? "bg-red-500/20" : severity === "warning" ? "bg-amber-500/20" : "bg-green-500/20"
            }`}>
              <Crown className={`w-6 h-6 ${
                severity === "critical" ? "text-red-400" : severity === "warning" ? "text-amber-400" : "text-green-400"
              }`} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-100">Diagnostic Position #1</h2>
              <p className="text-sm text-slate-400">Analyse de votre visibilite Google Ads</p>
            </div>
          </div>
          <button onClick={onRefresh} disabled={loading} data-testid="refresh-data"
            className="flex items-center gap-2 px-3 py-2 bg-slate-800/80 hover:bg-slate-700/80 rounded-lg text-xs text-slate-300 border border-slate-700/50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>

        {/* Position gauge */}
        <div className="flex items-center gap-6 mb-4">
          <div className="flex-1">
            <div className="flex items-baseline gap-2 mb-2">
              <span className={`text-4xl font-black ${
                posScore >= 50 ? "text-green-400" : posScore >= 20 ? "text-amber-400" : "text-red-400"
              }`}>{posScore}%</span>
              <span className="text-sm text-slate-400">du temps en position #1</span>
            </div>
            <div className="w-full h-3 bg-slate-700/50 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-700 ${
                posScore >= 50 ? "bg-green-500" : posScore >= 20 ? "bg-amber-500" : "bg-red-500"
              }`} style={{ width: `${posScore}%` }} />
            </div>
          </div>

          {blocker === "budget" && pd.max_budget_lost_pct > 15 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 max-w-xs">
              <div className="flex items-center gap-2 mb-1">
                <ShieldAlert className="w-4 h-4 text-red-400" />
                <span className="text-sm font-semibold text-red-300">Frein principal : Budget</span>
              </div>
              <p className="text-xs text-slate-400">
                <strong className="text-red-300">{pd.max_budget_lost_pct}%</strong> des impressions perdues par manque de budget.
                Augmenter le budget est la premiere action pour etre #1.
              </p>
            </div>
          )}
          {blocker === "rank" && pd.max_rank_lost_pct > 15 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 max-w-xs">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-amber-300">Frein principal : Rang/Qualite</span>
              </div>
              <p className="text-xs text-slate-400">
                <strong className="text-amber-300">{pd.max_rank_lost_pct}%</strong> des impressions perdues a cause du classement.
                Ameliorer le Quality Score et les encheres.
              </p>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-slate-200">{s.total_conversions}</p>
            <p className="text-[10px] text-slate-500">Conversions (30j)</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-slate-200">{s.avg_cpa}€</p>
            <p className="text-[10px] text-slate-500">CPA Moyen</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-slate-200">{s.total_clicks}</p>
            <p className="text-[10px] text-slate-500">Clics (30j)</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-slate-200">{pd.avg_impression_share || 0}%</p>
            <p className="text-[10px] text-slate-500">Part d'impressions</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CampaignPositionCard({ campaign: c, budgetRec, onApplyBudget, budgetApplied, budgetApplying }) {
  const is = c.impression_share || 0;
  const topIs = c.top_impression_share || 0;
  const absTop = c.abs_top_impression_share || 0;
  const budgetLost = c.budget_lost_share || 0;
  const rankLost = c.rank_lost_share || 0;

  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-5" data-testid={`camp-card-${c.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="font-semibold text-slate-200">{c.name}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] px-2 py-0.5 rounded bg-slate-700/60 text-slate-400">{c.bidding?.replace(/_/g, " ")}</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-slate-700/60 text-slate-400">{c.channel_type}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-center">
          <div>
            <p className="text-2xl font-black text-amber-300">{absTop}%</p>
            <p className="text-[10px] text-slate-500">Position #1</p>
          </div>
          <div className="h-8 w-px bg-slate-700" />
          <div>
            <p className="text-xl font-bold text-slate-300">{c.daily_budget}€</p>
            <p className="text-[10px] text-slate-500">Budget/jour</p>
          </div>
          {c.target_cpa > 0 && (
            <>
              <div className="h-8 w-px bg-slate-700" />
              <div>
                <p className="text-xl font-bold text-slate-300">{c.target_cpa}€</p>
                <p className="text-[10px] text-slate-500">Target CPA</p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <ProgressBar label="Visibilite totale" value={is} color="blue" />
        <ProgressBar label="En haut de page" value={topIs} color="teal" />
        <ProgressBar label="Position #1" value={absTop} color="amber" />
        {budgetLost > 3 && <ProgressBar label="Perdu (budget)" value={budgetLost} color="red" alert />}
        {rankLost > 3 && <ProgressBar label="Perdu (rang)" value={rankLost} color="orange" />}
      </div>

      {budgetLost > 20 && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-red-300 mb-0.5">
                {budgetLost}% des impressions perdues par manque de budget
              </p>
              <p className="text-xs text-slate-400">
                Budget recommande : <strong className="text-green-300">{Math.round(c.daily_budget / (1 - budgetLost / 100))}€/jour</strong> (actuel : {c.daily_budget}€)
              </p>
            </div>
          </div>
          {onApplyBudget && (
            <button onClick={onApplyBudget} disabled={budgetApplied || budgetApplying}
              className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium ${
                budgetApplied ? "bg-green-500/20 text-green-300" : "bg-red-500/20 hover:bg-red-500/30 text-red-200"
              } disabled:opacity-60`}
              data-testid={`camp-budget-apply-${c.id}`}>
              {budgetApplying ? <RefreshCw className="w-4 h-4 animate-spin" /> :
                budgetApplied ? <><Check className="w-4 h-4" /> Applique</> :
                <><ArrowUp className="w-4 h-4" /> Augmenter le budget</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PositionKPI({ icon, label, value, sub, status, invert }) {
  const styles = {
    good: { bg: "from-green-500/12 to-green-600/5 border-green-500/25", text: "text-green-400", icon: "text-green-400/70" },
    warn: { bg: "from-amber-500/12 to-amber-600/5 border-amber-500/25", text: "text-amber-400", icon: "text-amber-400/70" },
    bad: { bg: "from-red-500/12 to-red-600/5 border-red-500/25", text: "text-red-400", icon: "text-red-400/70" },
  };
  const st = styles[status] || styles.warn;
  return (
    <div className={`bg-gradient-to-br border rounded-xl p-4 ${st.bg}`}>
      <div className={`mb-2 ${st.icon}`}>{icon}</div>
      <p className={`text-2xl font-black ${st.text}`}>{value}</p>
      <p className="text-xs text-slate-400 mt-0.5">{label}</p>
      <p className="text-[10px] text-slate-500">{sub}</p>
    </div>
  );
}

function MiniKPI({ label, value, icon, color }) {
  const colors = { blue: "text-blue-300", green: "text-green-300", red: "text-red-300", amber: "text-amber-300", slate: "text-slate-300" };
  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-3 flex items-center gap-3">
      <div className={`${colors[color] || "text-slate-300"} opacity-60`}>{icon}</div>
      <div>
        <p className={`text-base font-bold ${colors[color]}`}>{value}</p>
        <p className="text-[10px] text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function ActionPriority({ num, title, desc, active, critical }) {
  return (
    <div className={`flex items-center gap-4 p-3 rounded-lg ${
      critical ? "bg-red-500/10 border border-red-500/20" :
      active ? "bg-amber-500/10 border border-amber-500/15" :
      "bg-slate-800/40 border border-slate-700/30 opacity-50"
    }`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
        critical ? "bg-red-500/30 text-red-200" :
        active ? "bg-amber-500/20 text-amber-300" :
        "bg-slate-700/50 text-slate-500"
      }`}>{num}</div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${active ? "text-slate-200" : "text-slate-500"}`}>{title}</p>
        <p className="text-xs text-slate-400">{desc}</p>
      </div>
      {critical && <span className="text-[10px] uppercase tracking-wider font-bold text-red-300 bg-red-500/20 px-2 py-0.5 rounded">Critique</span>}
      {active && !critical && <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />}
      {!active && <Check className="w-4 h-4 text-green-500/50 shrink-0" />}
    </div>
  );
}

function PositionBadge({ pct }) {
  if (!pct && pct !== 0) return <span className="text-slate-600">-</span>;
  const color = pct >= 70 ? "bg-green-500/20 text-green-300" : pct >= 30 ? "bg-amber-500/20 text-amber-300" : pct > 0 ? "bg-red-500/20 text-red-300" : "bg-slate-700/30 text-slate-500";
  return <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${color}`}>{pct}%</span>;
}

function ProgressBar({ label, value, color, alert: isAlert }) {
  const colors = { blue: "bg-blue-400", green: "bg-green-400", teal: "bg-teal-400", amber: "bg-amber-400", yellow: "bg-yellow-400", red: "bg-red-400", orange: "bg-orange-400" };
  return (
    <div className="flex items-center gap-2">
      <span className={`text-[11px] w-32 shrink-0 ${isAlert ? "text-red-300 font-semibold" : "text-slate-400"}`}>{label}</span>
      <div className="flex-1 h-2 bg-slate-700/50 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${colors[color]}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className={`text-xs font-bold w-12 text-right ${isAlert ? "text-red-300" : "text-slate-300"}`}>{value}%</span>
    </div>
  );
}

function RecoSection({ title, icon, color, count, totalSavings, onApplyAll, applyingAll, children }) {
  const [open, setOpen] = useState(true);
  const bc = { red: "border-red-500/25", green: "border-green-500/25", amber: "border-amber-500/25", orange: "border-orange-500/25" };
  return (
    <div className={`bg-slate-800/50 border ${bc[color] || "border-slate-700/50"} rounded-xl overflow-hidden`}>
      <div className="px-6 py-4 border-b border-slate-700/30 flex items-center justify-between">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 font-semibold text-base">
          {icon} {title} <span className="text-xs text-slate-400 font-normal">({count})</span>
          {totalSavings > 0 && <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300 ml-2">~{totalSavings.toFixed(0)}€</span>}
        </button>
        <div className="flex items-center gap-2">
          <button onClick={onApplyAll} disabled={applyingAll}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-xs font-medium disabled:opacity-50"
            data-testid={`apply-all-${color}`}>
            {applyingAll ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Tout appliquer
          </button>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400 cursor-pointer" onClick={() => setOpen(false)} /> : <ChevronDown className="w-4 h-4 text-slate-400 cursor-pointer" onClick={() => setOpen(true)} />}
        </div>
      </div>
      {open && <div className="p-4 space-y-2">{children}</div>}
    </div>
  );
}

function RecoRow({ rec, applied, applying, onApply, children }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg ${applied ? "bg-green-500/10 border border-green-500/20" : "bg-slate-900/40 border border-slate-700/20"}`}>
      <div className="flex-1 min-w-0">{children}</div>
      <button onClick={onApply} disabled={applied || applying}
        className={`ml-4 shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${applied ? "bg-green-500/20 text-green-300" : "bg-blue-500/20 hover:bg-blue-500/30 text-blue-300"} disabled:opacity-60`}>
        {applying ? <RefreshCw className="w-3 h-3 animate-spin" /> : applied ? <><Check className="w-3 h-3" /> Applique</> : <><Zap className="w-3 h-3" /> Appliquer</>}
      </button>
    </div>
  );
}

function EvolutionCell({ before, current, delta, unit = "", invert }) {
  const beforeVal = before != null ? (typeof before === "number" ? (Number.isInteger(before) ? before : before.toFixed(1)) : before) : "-";
  const currentVal = current != null ? (typeof current === "number" ? (Number.isInteger(current) ? current : current.toFixed(1)) : current) : "-";
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  // For CPA, lower is better (invert)
  const isGood = invert ? isNegative : isPositive;
  const isBad = invert ? isPositive : isNegative;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="flex items-center gap-1 text-xs">
        <span className="text-slate-500">{beforeVal}{unit}</span>
        <ArrowUpRight className="w-3 h-3 text-slate-600" />
        <span className="text-slate-200 font-medium">{currentVal}{unit}</span>
      </div>
      {delta != null && delta !== 0 && (
        <span className={`text-[10px] font-bold ${isGood ? "text-green-400" : isBad ? "text-red-400" : "text-slate-500"}`}>
          {isPositive ? "+" : ""}{typeof delta === "number" ? (Number.isInteger(delta) ? delta : delta.toFixed(1)) : delta}{unit}
        </span>
      )}
      {(delta === 0 || delta == null) && <span className="text-[10px] text-slate-600">=</span>}
    </div>
  );
}
